import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, IconButton, Theme, WithStyles, withStyles, WithTheme } from '@material-ui/core';
import { darken } from '@material-ui/core/styles/colorManipulator';
import { ArrowBackIos as ArrowBackIcon, Close as CloseIcon, MoreVert as MoreVertIcon } from '@material-ui/icons';
import classnames from 'classnames';

import { Button, TypographyWithIcon } from 'components/v2';
import Header from 'containers/Header';
import { HeaderProps } from 'containers/Header/Header';

import AvatarLumaBot from '../../components/AvatarLumaBot';
import { useChatContext } from '../../contexts/ChatContext';

import BadgeOnline from './BadgeOnline';
import IconButtonLumaBotActions from './IconButtonLumaBotActions';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      background: `linear-gradient(180deg, ${darken(theme.palette.primary.main, 0.1)} 0%, ${
        theme.palette.primary.main
      } 100%)`,
      height: 56,
      wordBreak: 'break-all',
    },
    avatar: {
      border: `2px solid ${theme.palette.common.white}`,
    },
    marginLeft: {
      marginLeft: theme.spacing.unit,
    },
    toolbar: {
      minHeight: 56,
      padding: `0 ${theme.spacing.unit * 1.5}px 0 ${theme.spacing.unit * 2.5}px`,
      color: theme.palette.primary.contrastText,
    },
    wrapperActionIcon: {
      padding: theme.spacing.unit,
    },
  });

const messages = defineMessages({
  back: {
    id: 'components.v2.AppBarLumaBot.back',
    defaultMessage: 'Back',
  },
});

export interface AppBarLumaBotProps
  extends WithStyles<typeof styles>,
    WithTheme,
    Omit<HeaderProps, 'classes' | 'width'> {
  onClose: () => void;
}

const AppBarLumaBot: React.FC<AppBarLumaBotProps> = ({ classes, theme, onClose, ...rest }) => {
  const { settings, mode, isPopper, setMode } = useChatContext();
  const { formatMessage } = useIntl();

  return (
    <Header
      className={classes.root}
      toolbarProps={{ className: classes.toolbar }}
      logo={
        mode === 'blocked' ? (
          <Button
            data-lh-id="lumabot-header-btn-back"
            color="inherit"
            icon={<ArrowBackIcon fontSize="small" />}
            onClick={() => setMode('default')}
          >
            {formatMessage(messages.back)}
          </Button>
        ) : (
          <TypographyWithIcon
            component="span"
            variant="bW600F17"
            color="inherit"
            extraMargin
            icon={
              <BadgeOnline>
                <AvatarLumaBot src={settings.avatar as string} size="medium" className={classes.avatar} />
              </BadgeOnline>
            }
          >
            {settings.header}
          </TypographyWithIcon>
        )
      }
      color="primary"
      elevation={0}
      secondaryAction={
        <>
          {mode !== 'blocked' && (
            <IconButtonLumaBotActions
              data-lh-id="lumabot-header-btn-actions"
              className={classnames(classes.marginLeft, classes.wrapperActionIcon)}
              color="inherit"
            >
              <MoreVertIcon />
            </IconButtonLumaBotActions>
          )}
          {isPopper && (
            <IconButton
              data-lh-id="lumabot-header-btn-close"
              onClick={() => onClose()}
              className={classes.wrapperActionIcon}
              color="inherit"
            >
              <CloseIcon />
            </IconButton>
          )}
        </>
      }
      {...rest}
    />
  );
};

export default withStyles(styles, { name: 'AppBarLumaBot', withTheme: true })(AppBarLumaBot);
